/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { IBlockedUser, IReqBlockedUser } from '@dataTypes/blocked-user.type'
import {
  createBlockedUser,
  deleteBlockedUser,
  getAllBlockedUsers,
  updateBlockedUserById,
} from '@services/api/blocked-user.api'
import { SET_LAST_BLOCKED_USER } from '@store/reducers/messageCenter.reducer'
import { fetchAllConversations } from './chat.action'

export const fetchAllBlockedUsers = createAsyncThunk<
  IBlockedUser[],
  void,
  {
    rejectValue: string
  }
>('api/blocked-user/fetch/all', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllBlockedUsers()

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'blocked_users' in response.data &&
      Array.isArray(response.data.blocked_users)
    ) {
      const blockedUsers = response.data.blocked_users as IBlockedUser[]
      return blockedUsers
    }

    return rejectWithValue('Blocked users not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addBlockedUser = createAsyncThunk<
  IBlockedUser,
  IReqBlockedUser,
  { rejectValue: string }
>(
  'api/blocked-user/add',
  async (userDetails, { dispatch, rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createBlockedUser({
        ...userDetails,
        created_by: user.user_id,
      })

      if (response.statusCode !== 201) {
        return rejectWithValue(
          response.message || 'Failed to create new blocked user',
        )
      }

      if (response.data && 'blocked_user' in response.data) {
        const blockedUser = response.data.blocked_user as IBlockedUser
        if (blockedUser.blocked_user_id)
          dispatch(SET_LAST_BLOCKED_USER(blockedUser.blocked_user_id))
        return blockedUser
      }
      return rejectWithValue('Blocked user not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create blocked user')
    }
  },
)

export const updateBlockedUser = createAsyncThunk<
  void,
  { blocked_user_id: string; data: Partial<IBlockedUser> },
  { rejectValue: string }
>('api/blocked-user/update', async (req, { dispatch, rejectWithValue }) => {
  try {
    const response = await updateBlockedUserById(req.blocked_user_id, req.data)

    if (response.statusCode !== 200) {
      return rejectWithValue(
        response.message || 'Failed to update blocked user',
      )
    }

    if (response.data && 'blocked_user' in response.data) {
      dispatch(fetchAllConversations())
    }
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create blocked user')
  }
})

export const unblockUser = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/blocked-user/unblock', async (blockedUserId, { rejectWithValue }) => {
  try {
    const response = await deleteBlockedUser(blockedUserId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message || 'Failed to unblock user')
    }
    return blockedUserId
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to unblock user')
  }
})
